import React, { useState, useEffect } from 'react';
import { Typography, Paper, Box, Grid, CircularProgress } from '@mui/material';
import { Link } from 'react-router-dom';
import PeopleIcon from '@mui/icons-material/People';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import Takkevariheader from './Takkevariheader';
import { getFirestore, collection, getDocs, query, where } from 'firebase/firestore';
import { toast } from 'react-toastify';

const db = getFirestore();

const DashboardCard = ({ title, count, icon, color, link, isLoading }) => (
    <Link to={link} style={{ textDecoration: 'none' }}>
        <Paper
            elevation={4}
            sx={{
                height: 130,
                borderRadius: 2,
                p: 2,
                backgroundColor: color,
                color: '#fff',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                transition: '0.3s',
                cursor: 'pointer',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                    opacity: 0.95,
                },
            }}
        >
            <Box>
                <Typography variant="subtitle1">{title}</Typography>
                {isLoading ? (
                    <CircularProgress size={30} sx={{ color: '#fff' }} />
                ) : (
                    <Typography variant="h5" fontWeight="bold">
                        {count}
                    </Typography>
                )}
            </Box>
            <Box
                sx={{
                    fontSize: 50,
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    borderRadius: 2,
                    p: 1,
                    height: 70,
                    lineHeight: 1,
                }}
            >
                {icon}
            </Box>
        </Paper>
    </Link>
);

const Takkevarihome = () => {
    const [counts, setCounts] = useState({
        customers: 0,
        amountGiven: 0,
        interestTaken: 0,
    });
    const [loading, setLoading] = useState(true);

    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            const customersCollection = collection(db, 'takkevaricusts');
            const loansCollection = collection(db, 'takkevariloans');
            const interestsCollection = collection(db, 'takkevariintrests');
            
            const [
                customersSnapshot,
                loansSnapshot,
                interestsSnapshot,
            ] = await Promise.all([
                getDocs(customersCollection),
                getDocs(loansCollection),
                getDocs(interestsCollection),
            ]);

            const customersCount = customersSnapshot.size;
            
            const totalAmountGiven = loansSnapshot.docs.reduce((sum, doc) => {
                const amount = doc.data().amount || 0;
                return sum + amount;
            }, 0);

            // Fetch only 'paid' interests for the total count
            const paidInterestsQuery = query(interestsCollection, where('status', '==', 'Paid'));
            const paidInterestsSnapshot = await getDocs(paidInterestsQuery);
            const totalInterestTaken = paidInterestsSnapshot.docs.reduce((sum, doc) => {
                const interestAmount = doc.data().interestAmount || 0;
                return sum + interestAmount;
            }, 0);

            setCounts({
                customers: customersCount,
                amountGiven: totalAmountGiven,
                interestTaken: totalInterestTaken,
            });

        } catch (error) {
            console.error("Error fetching dashboard data:", error);
            toast.error("Failed to fetch dashboard data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, []);

    // Helper function to format currency
    const formatCurrency = (amount) => {
        return `₹${new Intl.NumberFormat('en-IN', {
            maximumFractionDigits: 0
        }).format(amount)}`;
    };

    return (
        <Box>
            <Takkevariheader />
            <Typography variant="h5" gutterBottom>
                <b>Takkevari Dashboard</b>
            </Typography>
            <Grid container className="row">
                {/* Customers */}
                <Grid className="col-sm-3 mb-3" item xs={12} sm={6} md={3}>
                    <DashboardCard
                        title="टोटल खातेदार "
                        count={counts.customers}
                        icon={<PeopleIcon fontSize="inherit" />}
                        color="#1976d2"
                        // link="/takkevari/customers"
                        isLoading={loading}
                    />
                </Grid>

                {/* Amount Given */}
                <Grid className="col-sm-3 mb-3" item xs={12} sm={6} md={3}>
                    <DashboardCard
                        title="टोटल घेतलेली  रक्कम "
                        count={formatCurrency(counts.amountGiven)}
                        icon={<MonetizationOnIcon fontSize="inherit" />}
                        color="#6a1b9a"
                        // link="/takkevari/loans"
                        isLoading={loading}
                    />
                </Grid>

                {/* Interest Taken */}
                <Grid className="col-sm-3 mb-3" item xs={12} sm={6} md={3}>
                    <DashboardCard
                        title="टोटल व्याज जमा "
                        count={formatCurrency(counts.interestTaken)}
                        icon={<MoneyOffIcon fontSize="inherit" />}
                        color="#0288d1"
                        // link="/takkevari/interest-taken"
                        isLoading={loading}
                    />
                </Grid>

                {/* Reports */}
                <Grid className="col-sm-3 mb-3" item xs={12} sm={6} md={3}>
                    <DashboardCard
                        title="रिपोर्ट्स "
                        count="View"
                        icon={<ShowChartIcon fontSize="inherit" />}
                        color="#2e7d32"
                        link="/takkevari/reports/amounts"
                        isLoading={false} // This card doesn't need to load a count
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default Takkevarihome;